import React, { useEffect, useMemo, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { useMutation, useQuery } from 'react-query';
import PropTypes from 'prop-types';
import PartnerProfileService from 'api/PartnerProfileService';
import { ModalDialog } from 'components/ModalDialog/ModalDialog';
import AddContentBlock from './AddContentBlock/AddContentBlock';
import HTMLBlockEditor from './ContentEditors/HTMLBlockEditor';
import FileBlockEditor from './ContentEditors/FileBlockEditor/FileBlockEditor';
import ItemBlockEditor from './ContentEditors/ItemBlockEditor/ItemBlockEditor';
import { BlockList } from './BlockList';
import { BlockListContext } from './BlockList/BlockListContext';

const editors = {
  html: HTMLBlockEditor,
  files: FileBlockEditor,
  item_list: ItemBlockEditor,
};

const propTypes = {
  partnerId: PropTypes.string,
  allowedBlocks: PropTypes.arrayOf(PropTypes.string),
};
const defaultProps = {
  partnerId: '',
  allowedBlocks: [],
};

export default function ContentBlockList(props) {
  const { partnerId, allowedBlocks } = props;
  const [blockItems, setBlockItems] = useState([]);
  const [isStateUpdated, setStateUpdated] = useState(false);
  const [createContentBlock, setCreateContentBlock] = useState(false);
  const [toManageBlock, setToManageBlock] = useState(null);
  const [deleteBlockId, setDeleteBlockId] = useState('');
  const [deletedBlocks, setDeletedBlocks] = useState([]);
  const [reOrderBlocks, setReOrderBlocks] = useState(false);
  const [open, setOpen] = useState(false);
  const [contentParam, setContentParam] = useState('');
  const blockListQuery = useQuery(
    ['partnerProfile', partnerId],
    () => PartnerProfileService.getBlocks(partnerId),
    {
      enabled: false,
      cacheTime: 0,
      onSuccess: (response) => {
        setBlockItems([]);
        if (
          response.data &&
          response.data.blocks &&
          response.data.blocks.items
        ) {
          // if(allowedBlocks.find())
          setBlockItems(response.data.blocks.items);
          setStateUpdated(true);
        }
      },
    }
  );

  useEffect(() => {
    blockListQuery.refetch();
  }, [partnerId]);

  useEffect(() => {
    if (isStateUpdated) setStateUpdated(false);
  }, [isStateUpdated]);

  const mutationOrderBlocks = useMutation(
    (blocks) => PartnerProfileService.orderBlocks(partnerId, blocks),
    {
      onSuccess: () => {
        setReOrderBlocks(false);
      },
      onError: () => {
        setReOrderBlocks(false);
      },
    }
  );

  const mutationDeleteBlock = useMutation(
    (blockId) => PartnerProfileService.deleteBlock(blockId),
    {
      onSuccess: () => {
        blockListQuery.refetch();
        setDeleteBlockId('');
      },
    }
  );

  const onCancel = () => {
    setToManageBlock(null);
    setCreateContentBlock(false);
    // document.getElementById('add-content-block-form').classList.add('d-none');
  };

  const onCreateContentBlock = (data) => {
    setToManageBlock(data);
    // document.getElementById('add-content-block-form').classList.add('d-none');
    setCreateContentBlock(false);
  };

  const onContentBlockEditorCancel = () => {
    onCancel();
  };

  const onSaveSuccessfully = () => {
    blockListQuery.refetch();
    setCreateContentBlock(false);
  };

  const onAddContentBlock = () => {
    setCreateContentBlock(true);
    // document.getElementById('add-content-block-form').classList.remove('d-none');
  };

  const onDeleteContentBlock = (e, id) => {
    e.preventDefault();
    setOpen(true);
    setContentParam(id);
  };

  const onDeleteConfirm = () => {
    setDeleteBlockId(contentParam);
    deletedBlocks.push(contentParam);
    setDeletedBlocks(deletedBlocks);
    mutationDeleteBlock.mutate(contentParam);
    // console.log(contentParam);
    setOpen(false);
  };

  const onEditBlock = (e, block) => {
    e.preventDefault();
    setToManageBlock(block);
  };

  const onSortEnd = (items) =>
    mutationOrderBlocks.mutate(items.map((b) => b.id));

  const renderBlockList = () => {
    let result;
    const actions = useMemo(
      () => ({
        onClickEdit: onEditBlock,
        onClickDelete: onDeleteContentBlock,
      }),
      []
    );
    const dataSource = blockItems.filter(
      (item) => !deletedBlocks.some((d) => d.id === item.id)
    );

    if (!blockListQuery.isLoading) {
      if (dataSource.length > 0) {
        result = (
          <BlockListContext.Provider value={actions}>
            <BlockList
              dataSource={dataSource}
              onSortEnd={onSortEnd}
              reOrderBlocks={reOrderBlocks}
              deleteBlockId={deleteBlockId}
              isStateUpdated={isStateUpdated}
            />
          </BlockListContext.Provider>
        );
      } else {
        result = (
          <p className="text-center text-muted">
            No content blocks has been added.
          </p>
        );
      }
    } else {
      result = (
        <div className="text-muted text-center m-0">
          <Spinner size="sm" animation="border" /> Loading...
        </div>
      );
    }

    return result;
  };

  const renderBlockEditor = () => {
    let result;

    if (toManageBlock) {
      const Editor = editors[toManageBlock?.contentType];

      result = (
        <Editor
          onCancel={onContentBlockEditorCancel}
          partnerId={partnerId}
          contentBlock={toManageBlock}
          onSaveSuccessfully={onSaveSuccessfully}
        />
      );
    }

    return result;
  };

  return (
    <>
      <div
        className="partner-profile-page-content mt-4"
        data-testid="Wrapper-contentBlock"
      >
        <div className="d-flex mb-3">
          <h3 className="page-content-header mb-0">Profile Page Content</h3>
          <div className="flex-grow-1 d-flex align-content-center align-items-center justify-content-end  text-end">
            <Button
              variant="outline-primary"
              onClick={onAddContentBlock}
              data-testid="Button-add"
            >
              Add Content Block
            </Button>
          </div>
        </div>
        <hr />
        {renderBlockList()}
      </div>
      {/* <div id="add-content-block-form" className="d-none"> */}
      {createContentBlock && (
        <AddContentBlock
          onCancel={onCancel}
          onCreate={onCreateContentBlock}
          allowedBlocks={allowedBlocks}
        />
      )}
      {/* </div> */}
      {renderBlockEditor()}
      <ModalDialog
        open={open}
        setOpen={setOpen}
        header="Delete Content Block"
        message="You are about to delete a content block. Are you sure about it?"
        onConfirm={onDeleteConfirm}
        dataTestid="Modal-deleteContentBlock"
      />
    </>
  );
}
ContentBlockList.propTypes = propTypes;
ContentBlockList.defaultProps = defaultProps;
